import { AlertTriangle, Image as ImageIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnimatedImage } from './animated-image';
import { WaveformPlayer } from './sound-waveform-player';

import { assetsApi, peekAsset, type AssetSummary } from '@/lib/assets-api';

/** Shared between every Ordering shape (vertical/horizontal's SortableCustomOrderRow, circular's
 * own item tiles, and the inert collapsed-card preview) — lives in its own module rather than
 * inside ordering-block.tsx or ordering-circle.tsx specifically, since those two files already
 * import from each other (ordering-block.tsx renders <OrderingCircle>) and either direction would
 * create a circular import. */
export interface OrderingItemMedia {
  kind: 'image' | 'audio';
  assetId: string;
  altText?: string;
  motionDescription?: string;
  transcript?: string;
  /** Whether the asset moves, stored rather than resolved — see `MCQOption.media`. */
  animated?: boolean;
}

// Loosely based on MCQMediaOptionContent (mcq-block.tsx) — kept as a separate copy rather than a
// shared cross-block export, matching this codebase's established convention of not sharing tiny
// near-identical block-specific helpers across modules — but deliberately diverges on image
// sizing: an image renders at its own natural size (no forced height cap/crop), only ever
// constrained by `max-w-full` so it can't overflow past the row it sits in. The teacher chose this
// image and its size for a reason (a wide diagram, a tall portrait) — silently shrinking it to a
// fixed tile risks trimming information the image was meant to convey, and picking an
// appropriately-sized source image is the teacher's own responsibility, not something this widget
// should second-guess. Audio has no equivalent "natural size" (a waveform is a rendered
// visualization, not asset content with inherent dimensions), so it keeps one fixed rendering size
// throughout.
//
// Circular is the one exception to "as-is" sizing (see its own call site's `constrained` prop):
// items there sit at fixed geometric positions around a ring, so an oversized image would visually
// overlap its neighbors rather than just growing the row the way a linear list's flex layout
// naturally accommodates — a structural constraint, not a preference, so circular passes
// `constrained` to cap image height instead.
//
// `fillWidth` is the THIRD sizing case, and it is the opposite problem: in a sort laid out in
// COLUMNS every item sits in a narrow bin of known width, so "as-is" leaves a small photo as a
// thumbnail in a column with room to spare, and a height cap shrinks it further. There the card's
// width IS the right size — not the image's natural width, which is a property of the file and not
// of the layout. It takes the compact chrome `constrained` brings and replaces the height cap,
// since a cap and a fill are the same measurement pulling opposite ways.
export function OrderingItemMediaContent({ media, constrained, fillWidth, ownerName }: { media: OrderingItemMedia; constrained?: boolean; fillWidth?: boolean;
  /** The item's own name, so an audio item's Play button says which clip it plays rather than
   *  every one of them announcing a bare "Play". */
  ownerName?: string }) {
  const { t } = useTranslation();
  // Seeded from the cache — see `peekAsset`, and image-block for the why.
  const [asset, setAsset] = useState<AssetSummary | null>(() => peekAsset(media.assetId) ?? null);
  const [loadFailed, setLoadFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setLoadFailed(false);
    assetsApi.get(media.assetId)
      .then((a) => { if (!cancelled) setAsset(a); })
      .catch((err) => {
        if (cancelled) return;
        console.error('OrderingItemMediaContent: failed to load asset', media.assetId, err);
        setLoadFailed(true);
      });
    return () => { cancelled = true; };
  }, [media.assetId]);

  // Fixed square — only ever used for a generic placeholder/error icon, never real image content,
  // so "as-is" sizing doesn't apply here (there's no real content whose size to honor).
  /* Compact chrome for BOTH narrow modes — a ring item and a bin column each sit in a space the
     layout chose, and only the image sizing differs between them. */
  const compact = constrained || fillWidth;
  const placeholderCls = compact ? 'h-10 w-10' : 'h-16 w-16';
  if (loadFailed) {
    /*
     * NAMED, because this placeholder is sometimes the WHOLE of a control.
     *
     * An ordering or sort item whose media is all it has becomes a button containing only this
     * div and an `aria-hidden` triangle — no text anywhere — so the control had no accessible
     * name at all and a screen reader announced "button" and stopped. Found by scanning the
     * editor with a block whose asset no longer resolved, which is not a probe-only state: a
     * deleted asset, a revoked share or a dropped connection all land here in front of a
     * student.
     *
     * The teacher's own alt text where there is one, since that is what the thing WAS, and a
     * plain statement otherwise. The triangle stays `aria-hidden`: it is the same information
     * in a second form, and `feedback_error_symbol_not_colour_alone` is about sighted readers.
     */
    return (
      <div
        role='img'
        aria-label={media.altText?.trim() || t('blocks.options.mediaUnavailable')}
        className={`flex ${placeholderCls} shrink-0 items-center justify-center rounded-md bg-ws-error-bg`}
      >
        <AlertTriangle className='h-4 w-4 text-ws-error-fg' aria-hidden='true' />
      </div>
    );
  }

  if (media.kind === 'image') {
    const src = asset?.urls.display ?? asset?.urls.thumb ?? asset?.urls.original;
    // The alt text is REQUIRED of the teacher — `media-alt-text-missing` is an error, not a warning
    // — and until now it was written and then discarded: `alt=''` marked the image decorative, so a
    // screen reader announced the item as nothing but its drag label, and there was no tooltip
    // either. An ordering item's image IS its content (a media item has no text of its own), so it
    // is never decorative. `title` as well as `alt` because, unlike a text item, an image has no
    // visible words to fall back on at any size.
    const altText = media.altText?.trim() || undefined;
    const imgCls = `h-auto max-w-full min-w-0 rounded-md ${fillWidth ? 'w-full' : compact ? 'max-h-16' : ''}`;
    return src ? (
      /*
       * UNLIKE MCQ, the description goes on the image: an ordering item's image carries its own
       * alt (it IS the item's content, never decorative), so a reader reaches it and the
       * alt-then-description pairing works here exactly as it does in the Image block.
       *
       * `autoplay={false}`, since a list of items is the same competing-loops problem an answer
       * list has, and `still` when constrained for the same reason MCQ's compact tile is.
       */
      asset ? (
        <AnimatedImage
          asset={asset}
          alt={altText ?? ''}
          motionDescription={media.motionDescription}
          autoplay={false}
          still={compact}
          align='start'
          fill={fillWidth}
          className={imgCls}
        />
      ) : (
        <img src={src} alt={altText ?? ''} title={altText} className={imgCls} />
      )
    ) : (
      <div className={`flex ${placeholderCls} shrink-0 items-center justify-center rounded-md bg-ws-hover`}>
        <ImageIcon className='h-4 w-4 text-ws-muted' aria-hidden='true' />
      </div>
    );
  }
  return (
    <WaveformPlayer
      src={asset?.urls.audio}
      t={t}
      height={compact ? 28 : 48}
      // `compact` drops WaveformPlayer's own padded/backgrounded box (this row/tile already has
      // its own), which is what makes 60px workable here — the un-compacted width (88px) plus that
      // extra chrome needed ~156px on its own, before even reaching a circular tile's fixed width.
      width={compact ? 60 : 160}
      interactive={false}
      showSpeed={false}
      hideSkip
      ownerName={ownerName}
      compact={compact}
    />
  );
}
